import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useEffect, useState } from "react";
import { getUpgradeMessageForBlockedTaskTypes } from "@common/constants/taskTypePlanRequirements";
import { Button } from "@design-system/Button/Button";
import { CheckIcon, RevertIcon } from "@icons";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import type { ApplyCloseTemplateEntitySelectionRow } from "../applyCloseTemplateEntitySelection.types";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface ApplyCloseEntitySelectCallbacks {
    onToggleRows: (rowIds: string[], selected: boolean) => void;
}

type ApplyCloseEntitySelectMeta = ViewTableMeta<
    ApplyCloseEntitySelectCallbacks,
    never
>;

export const ApplyCloseEntitySelectCell = memo(
    function ApplyCloseEntitySelectCell({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ApplyCloseTemplateEntitySelectionRow,
        unknown
    >) {
        const meta = (table.options.meta ?? {}) as ApplyCloseEntitySelectMeta;
        const onToggleRows = meta.callbacks?.onToggleRows;

        const item = row.original;
        // isApplicationSelected is embedded in row.original by the parent component,
        // so changes flow through row.original identity → ViewTableRowComponent memo
        // comparator detects change → cell re-renders correctly.
        const isSelected = item.isApplicationSelected ?? false;

        const [isHovered, setIsHovered] = useState(false);

        // Reset hover state when selection changes — if the user hovers over
        // the check-mark button and clicks to deselect, onMouseLeave never
        // fires (the button unmounts), leaving isHovered stale.
        useEffect(() => {
            setIsHovered(false);
        }, [isSelected]);

        const isClientRow = item.isClientRow;
        const isPlanBlocked = item.isPlanBlocked ?? false;
        const isAlreadyScheduled = item.isAlreadyScheduled ?? false;
        const toggleIds = isClientRow
            ? (item.children
                  ?.filter((c) => !c.isSelectionDisabled)
                  .map((c) => c.id) ?? [])
            : [row.id];
        const selectLabel = isClientRow ? "Select all" : "Select";
        const unselectTooltip = isClientRow ? "Unselect all" : "Unselect";
        const isSelectionDisabled = item.isSelectionDisabled ?? false;

        if (isSelectionDisabled) {
            const upgradeMessage = isPlanBlocked
                ? getUpgradeMessageForBlockedTaskTypes(
                      item.blockedTaskTypes ?? [],
                  )
                : null;
            const hasAlreadyScheduledChildren =
                isClientRow &&
                (item.children?.every((c) => c.isAlreadyScheduled) ?? false);
            const alreadyScheduledTooltip = isClientRow
                ? "This template is already scheduled for all entities"
                : "This template is already scheduled for this entity";
            const disabledTooltip =
                upgradeMessage ??
                (isAlreadyScheduled || hasAlreadyScheduledChildren
                    ? alreadyScheduledTooltip
                    : "Unavailable");
            return (
                <ViewTableCell captureClicks>
                    <Tooltip content={disabledTooltip}>
                        <Button variant="primary" size="sm" disabled>
                            {selectLabel}
                        </Button>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        if (isSelected) {
            return (
                <ViewTableCell captureClicks>
                    <Tooltip content={unselectTooltip}>
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            aria-label={unselectTooltip}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            onClick={() => onToggleRows?.(toggleIds, false)}
                        >
                            {isHovered ? <RevertIcon /> : <CheckIcon />}
                        </Button>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <Button
                    variant="primary"
                    size="sm"
                    className={viewTableStyles.showOnHover}
                    onClick={() => onToggleRows?.(toggleIds, true)}
                >
                    {selectLabel}
                </Button>
            </ViewTableCell>
        );
    },
);
